<script setup lang="ts">

import {computed, defineAsyncComponent, ref, watch} from "vue";
import {CssClasses} from "../../types/ui";

export type FormInputType =
    | 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search'
    | 'date' | 'datetime-local' | 'time' | 'month' | 'week' | 'hidden'
    | 'textarea' | 'htmlarea';

const props = withDefaults(defineProps<{
  type?: FormInputType;
  cssClass?: CssClasses;
  id?: string|Promise<string>;
  placeholder?: string;
  disabled?: boolean;
  autocomplete?: string;
}>(), {
  type: 'text',
  disabled: false,
});

// const model = defineModel<string>();

const [model, modifiers] = defineModel<string | number, 'number'>({
	set(value) {
		if (modifiers.number) {
			const n = parseFloat(value as string);
			return isNaN(n) ? value : n;
		}
		return value;
	}
});

const isTextarea = computed(() => props.type === 'textarea');
const isHtmlarea = computed(() => props.type === 'htmlarea');

// Tiptap (the rich-text editor behind htmlarea) is a large dependency most inputs never touch —
// load it as its own chunk only when an htmlarea is actually rendered.
const FormHtmlarea = defineAsyncComponent(() => import('./FormHtmlarea.vue'));

const resolvedId = ref<string>();

watch(
	() => props.id,
	async () => {
		resolvedId.value = await props.id;
	},
);

</script>

<template>
  <textarea
      v-if="isTextarea"
      :id="resolvedId"
      :class="['form-control', props.cssClass]"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :autocomplete="props.autocomplete"
      v-model="model"
  ></textarea>

  <FormHtmlarea
      v-else-if="isHtmlarea"
      v-model="model"
      :id="resolvedId"
      :css-class="props.cssClass"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
  />

  <input
      v-else
      :id="resolvedId"
      :type="props.type"
      :class="['form-control', props.cssClass]"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :autocomplete="props.autocomplete"
      v-model="model"
  />
</template>

<style scoped>

</style>
